import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { NostrFilter } from '@nostrify/nostrify';
import { WIKI_ARTICLE_KIND } from '@/lib/nip54';
import { PUBLICATION_CONTENT_KIND, PUBLICATION_INDEX_KIND } from '@/lib/nkbip01';
import { DELETION_KIND, REACTION_KIND, isLikeReaction } from '@/lib/nip25';
import { HIGHLIGHT_KIND, isValidHighlight } from '@/lib/nip84';
import {
  ECOSYSTEM_TARGET_KINDS,
  getActionTarget,
  mergeActivity,
  type ActivityItem,
  type ActivityPage,
} from '@/lib/activity';

/**
 * The ecosystem-wide activity feed: new articles, forks, edits, new
 * publications, stars, and highlights — merged into one chronological
 * list, newest first, paged backwards in time with Nostr's `until`.
 *
 * Each page is three relay filters in a single query (one round trip):
 * content events (`30818` + `30040`), reactions to ecosystem content
 * (filtered server-side by NIP-25's `k` tag), and highlights. Two
 * follow-up queries per page honor NIP-09 unstars/retractions and resolve
 * star/highlight targets for their titles — the same pattern
 * `useTopStarredArticles` uses for the leaderboard.
 *
 * Note the `#k` filter means stars written by clients that don't set a
 * `k` tag on their reactions never enter the feed (Wikistar always sets
 * one — see `buildStarTags`). That's an accepted trade-off: there's no
 * relay-level way to find "reactions to any wiki article" without it.
 */
export function useActivityFeed() {
  const { nostr } = useNostr();

  const query = useInfiniteQuery({
    queryKey: ['activity-feed'],
    queryFn: async ({ pageParam, signal: querySignal }): Promise<ActivityPage> => {
      const signal = AbortSignal.any([querySignal, AbortSignal.timeout(8000)]);

      const filters: NostrFilter[] = [
        { kinds: [WIKI_ARTICLE_KIND, PUBLICATION_INDEX_KIND], limit: 40 },
        {
          kinds: [REACTION_KIND],
          '#k': ECOSYSTEM_TARGET_KINDS.map(String),
          limit: 80,
        },
        { kinds: [HIGHLIGHT_KIND], limit: 40 },
      ];
      if (pageParam) {
        for (const filter of filters) filter.until = pageParam;
      }

      const events = await nostr.query(filters, { signal });

      // Honor NIP-09 deletions retracting a star ("unstarring") or a
      // highlight. Only a deletion authored by the same pubkey as the
      // event it references counts — same rule as `useStars`/`useHighlights`.
      const retractables = events.filter(
        (e) => (e.kind === REACTION_KIND && isLikeReaction(e)) || isValidHighlight(e),
      );
      let deletedIds: string[] = [];
      if (retractables.length > 0) {
        const deletions = await nostr.query(
          [{ kinds: [DELETION_KIND], '#e': retractables.map((e) => e.id), limit: retractables.length + 50 }],
          { signal },
        );
        deletedIds = retractables
          .filter((r) =>
            deletions.some(
              (d) => d.pubkey === r.pubkey && d.tags.some(([n, v]) => n === 'e' && v === r.id),
            ),
          )
          .map((r) => r.id);
      }
      const deleted = new Set(deletedIds);

      // Resolve star/highlight targets so the feed can show what was
      // starred/highlighted. `a` tag coordinates batch into one filter per
      // target kind; bare `e` tag ids go into a single ids filter.
      const active = retractables.filter((e) => !deleted.has(e.id));
      const coordsByKind = new Map<number, { authors: Set<string>; ds: Set<string> }>();
      const ids = new Set<string>();
      for (const event of active) {
        const raw = getActionTarget(event);
        if (!raw) continue;
        if (raw.kind !== undefined && raw.pubkey && raw.d !== undefined && ECOSYSTEM_TARGET_KINDS.includes(raw.kind)) {
          const entry = coordsByKind.get(raw.kind) ?? { authors: new Set<string>(), ds: new Set<string>() };
          entry.authors.add(raw.pubkey);
          entry.ds.add(raw.d);
          coordsByKind.set(raw.kind, entry);
        } else if (raw.id) {
          ids.add(raw.id);
        }
      }

      const targetFilters: NostrFilter[] = [...coordsByKind.entries()].map(([kind, { authors, ds }]) => ({
        kinds: [kind],
        authors: [...authors],
        '#d': [...ds],
        limit: ds.size * 2 + 10,
      }));
      if (ids.size > 0) targetFilters.push({ ids: [...ids], limit: ids.size });

      const targets = targetFilters.length > 0 ? await nostr.query(targetFilters, { signal }) : [];

      return { events, deletedIds, targets };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.events.length === 0) return undefined;
      // `until` is inclusive — step back one second to avoid re-fetching
      // the oldest event of this page.
      return Math.min(...lastPage.events.map((e) => e.created_at)) - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 30_000,
  });

  const items: ActivityItem[] = useMemo(
    () => mergeActivity(query.data?.pages ?? []),
    [query.data?.pages],
  );

  return { ...query, items };
}
